/**
 * Unistyles runtime configuration.
 *
 * Imported ONCE from `index.js` (side-effect only). Must run BEFORE the first
 * primitive uses `StyleSheet.create(...)` from `react-native-unistyles`.
 *
 * The persona themes (student / parent / tutor) share identical component code — only token
 * values differ; switching persona at runtime is a one-line `UnistylesRuntime.setTheme(role)`
 * call (`useParentThemeScope`, at the entrance of the parent space).
 *
 * `parentDark`, `studentDark` et `tutorDark` sont les MÊMES thèmes sur la nuit : des thèmes de plus, pas un
 * mode. Le choix entre clair et sombre appartient à la coquille de l'espace — `useParentThemeScope`
 * pour le parent (sa préférence, Automatique par défaut, qui suit le système, derrière son
 * drapeau) ; `useStudentThemeScope` pour l'élève (sa préférence, Automatique par défaut aussi, Wajih
 * 19/10) — ici on se contente de les enregistrer. Les thèmes adaptatifs
 * d'Unistyles (`adaptiveThemes`) ne conviennent pas : ils imposent UNE paire `light` / `dark`,
 * alors que le DS a trois univers, dont deux ont un versant sombre.
 *
 * L'app démarre dans `initial`, pas dans un univers : c'est ce que peint une surface du DS montée
 * hors d'un espace, et il ne doit pas bouger quand un univers est redessiné (`theme/initial.ts`).
 */
import { StyleSheet } from "react-native-unistyles"

import { breakpoints } from "./theme/tokens/breakpoints"
import { initialTheme } from "./theme/initial"
import { parentTheme } from "./theme/parent"
import { parentDarkTheme } from "./theme/parentDark"
import { studentTheme } from "./theme/student"
import { studentDarkTheme } from "./theme/studentDark"
import { tutorTheme } from "./theme/tutor"
import { tutorDarkTheme } from "./theme/tutorDark"
import { installWebScrollbars } from "./utils/webScrollbars"

const appBreakpoints = breakpoints

type AppBreakpoints = typeof appBreakpoints
type AppThemes = {
    initial: typeof initialTheme
    student: typeof studentTheme
    studentDark: typeof studentDarkTheme
    parent: typeof parentTheme
    parentDark: typeof parentDarkTheme
    tutor: typeof tutorTheme
    tutorDark: typeof tutorDarkTheme
}

// TS module augmentation so StyleSheet.create((theme, rt) => ...) is fully typed.
declare module "react-native-unistyles" {
    export interface UnistylesThemes extends AppThemes {}
    export interface UnistylesBreakpoints extends AppBreakpoints {}
}

StyleSheet.configure({
    themes: {
        initial: initialTheme,
        student: studentTheme,
        studentDark: studentDarkTheme,
        parent: parentTheme,
        parentDark: parentDarkTheme,
        tutor: tutorTheme,
        tutorDark: tutorDarkTheme,
    },
    breakpoints: appBreakpoints,
    settings: {
        initialTheme: "initial",
    },
})

// Au web, les barres de défilement aux couleurs du thème (les variables que pose Unistyles) ; rien sur le natif.
installWebScrollbars()

export type { AppBreakpoints, AppThemes }
